import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { NAV_LINKS } from "@/data/site";
import { EASE } from "@/components/blocks";

const MOBILE_LINKS = [
  ...NAV_LINKS,
  { label: "Recovery", path: "/recovery", testid: "nav-link-recovery" },
  { label: "Pricing", path: "/pricing", testid: "nav-link-pricing" },
  { label: "Contact", path: "/contact", testid: "nav-link-contact" },
];

export const Wordmark = ({ className = "" }) => (
  <span className={`font-display text-lg font-black uppercase tracking-tight text-bone ${className}`}>
    Strength<span className="text-lime">Leap</span>
  </span>
);

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    if (window.__lenis) open ? window.__lenis.stop() : window.__lenis.start();
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          scrolled ? "border-b border-bone/10 bg-ink/85 backdrop-blur-xl" : "border-b border-transparent"
        }`}
      >
        <div className="mx-auto flex h-20 max-w-[1600px] items-center justify-between px-6 sm:px-10 lg:px-16">
          <Link to="/" data-testid="nav-logo" aria-label="StrengthLeap home">
            <Wordmark />
          </Link>

          <nav className="hidden items-center gap-9 lg:flex" aria-label="Primary">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.path}
                to={l.path}
                data-testid={l.testid}
                className="group relative font-mono text-[11px] uppercase tracking-[0.22em] text-bone/80 transition-colors duration-300 hover:text-bone"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-full origin-left scale-x-0 bg-lime transition-transform duration-300 group-hover:scale-x-100" />
              </Link>
            ))}
            <Link
              to="/start"
              data-testid="nav-cta-start"
              className="ml-3 bg-lime px-5 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-white transition-colors duration-300 hover:bg-limed"
            >
              Book Assessment
            </Link>
          </nav>

          <button
            data-testid="nav-mobile-menu-button"
            onClick={() => setOpen(!open)}
            className="flex h-11 w-11 items-center justify-center text-bone lg:hidden"
            aria-label={open ? "Close menu" : "Open menu"}
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-40 flex flex-col justify-between bg-ink px-8 pb-10 pt-32"
          >
            <nav className="flex flex-col gap-2" aria-label="Mobile">
              {MOBILE_LINKS.map((l, i) => (
                <motion.div
                  key={l.path + l.label}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: EASE }}
                >
                  <Link
                    to={l.path}
                    data-testid={`mobile-${l.testid}`}
                    className="group flex items-center justify-between border-b border-bone/10 py-4 font-display text-3xl font-black uppercase tracking-tight text-bone transition-colors hover:text-lime"
                  >
                    {l.label}
                    <ArrowUpRight className="h-6 w-6 text-ash transition-colors group-hover:text-lime" />
                  </Link>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="flex items-center justify-between"
            >
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ash">
                Recover. Build. Perform.
              </p>
              <Link
                to="/start"
                data-testid="mobile-nav-cta-start"
                className="bg-lime px-6 py-3.5 font-mono text-[11px] uppercase tracking-[0.22em] text-white"
              >
                Book Assessment
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
